import { ordinalName } from './projectionNaming';

export type Pt = [number, number];
export type Mat3 = number[]; // length 9, row-major
export interface Dims { width: number; height: number }

/** Solve an 8x8 linear system A x = b (Gaussian elimination, partial pivot). */
function solve8(A: number[][], b: number[]): number[] {
  const n = 8;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r;
    }
    [M[col], M[piv]] = [M[piv], M[col]];
    const d = M[col][col];
    if (Math.abs(d) < 1e-12) throw new Error('degenerate quad in perspectiveTransform');
    for (let c = col; c <= n; c++) M[col][c] /= d;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col];
      for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
    }
  }
  return M.map((row) => row[n]);
}

/**
 * JS equivalent of cv2.getPerspectiveTransform: the 3x3 homography mapping
 * each src[i] onto dst[i]. Both arrays must hold exactly 4 points.
 */
export function perspectiveTransform(src: Pt[], dst: Pt[]): Mat3 {
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i];
    const [u, v] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -x * u, -y * u]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -x * v, -y * v]); b.push(v);
  }
  const h = solve8(A, b);
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

/** Apply a 3x3 homography to a point, with the perspective divide. */
export function applyH(H: Mat3, p: Pt): Pt {
  const [x, y] = p;
  const w = H[6] * x + H[7] * y + H[8];
  return [(H[0] * x + H[1] * y + H[2]) / w, (H[3] * x + H[4] * y + H[5]) / w];
}

export const YOLO_MAX = 640;

function dist(a: Pt, b: Pt): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/** Side lengths of a quad given in TL,TR,BR,BL order. */
export function quadSideLengths(c: Pt[]): { top: number; bottom: number; left: number; right: number } {
  return {
    top: dist(c[0], c[1]),
    bottom: dist(c[3], c[2]),
    left: dist(c[0], c[3]),
    right: dist(c[1], c[2]),
  };
}

/**
 * Suggest perspective_width/height from a quad's real pixel side lengths.
 * `pixelDims` is the resolution the normalized points are measured against
 * (rack: initial_width/height; shelf/table: frame dims).
 * The 640 cap is a CEILING ONLY — small regions keep their real size and the
 * BE upscales them at inference (resize_to_yolo_shape).
 */
export function suggestPerspective(pointsNorm: Pt[], pixelDims: Dims): { perspective_width: number; perspective_height: number } {
  const c: Pt[] = pointsNorm.map(([x, y]) => [x * pixelDims.width, y * pixelDims.height]);
  const s = quadSideLengths(c);
  const pwRaw = (s.top + s.bottom) / 2;
  const phRaw = (s.left + s.right) / 2;
  const scale = Math.min(1, YOLO_MAX / Math.max(pwRaw, phRaw));
  return {
    perspective_width: Math.round(pwRaw * scale),
    perspective_height: Math.round(phRaw * scale),
  };
}

export interface GridPadding { top: number; bottom: number; left: number; right: number }
export type GridOrientation = 'bottom_to_top' | 'left_to_right';
export type GridNaming = 'ряд' | 'полка';

export interface GridParams {
  count: number;
  gapPx: number;
  padding: GridPadding;        // fractions 0..1 of the perspective rect dims
  orientation: GridOrientation;
  naming: GridNaming;
}

export interface GeneratedShelf {
  pointsNorm: Pt[];
  perspective_width: number;
  perspective_height: number;
  name: string;
  shelf_num_on_rack: number;
  bottom_points_indices: [number, number];
  top_points_indices: [number, number];
}

/**
 * Subdivide a rack into a grid of shelves. Bands are laid out as rectangles in
 * the rack's perspective space (pw x ph), then mapped back to image-normalized
 * coordinates through the homography rect->image — matching the BE warp exactly.
 * `pixelDims` is the resolution the rack's normalized points map against
 * (rack.initial_width/height, which equals the frame resolution).
 */
export function generateShelfGrid(
  rackPointsNorm: Pt[],
  pw: number,
  ph: number,
  pixelDims: Dims,
  params: GridParams,
): GeneratedShelf[] {
  if (!pw || !ph) throw new Error('generateShelfGrid: perspective_width/height required');
  const rackPx: Pt[] = rackPointsNorm.map(([x, y]) => [x * pixelDims.width, y * pixelDims.height]);
  const dstRect: Pt[] = [[0, 0], [pw, 0], [pw, ph], [0, ph]];
  const Hrect2img = perspectiveTransform(dstRect, rackPx); // rect-space -> image px

  const { padding, gapPx, count, orientation, naming } = params;
  const x0 = padding.left * pw, x1 = (1 - padding.right) * pw;
  const y0 = padding.top * ph, y1 = (1 - padding.bottom) * ph;

  const cornersFor = (rx0: number, ry0: number, rx1: number, ry1: number): Pt[] =>
    ([[rx0, ry0], [rx1, ry0], [rx1, ry1], [rx0, ry1]] as Pt[]).map((p) => {
      const [px, py] = applyH(Hrect2img, p);
      return [px / pixelDims.width, py / pixelDims.height] as Pt;
    });

  const out: GeneratedShelf[] = [];

  if (orientation === 'bottom_to_top') {
    const L = (y1 - y0) - (count - 1) * gapPx;
    const band = L / count;
    if (band <= 0) throw new Error('generateShelfGrid: gap/padding leave no room for shelves');
    const bandW = x1 - x0;
    for (let i = 0; i < count; i++) {
      const ry0 = y0 + i * (band + gapPx);
      const ry1 = ry0 + band;
      const num = count - i; // i=0 is topmost band; bottom band = num 1
      out.push({
        pointsNorm: cornersFor(x0, ry0, x1, ry1),
        perspective_width: Math.round(bandW),
        perspective_height: Math.round(band),
        name: ordinalName(num, naming),
        shelf_num_on_rack: num,
        bottom_points_indices: [2, 3],
        top_points_indices: [0, 1],
      });
    }
  } else {
    const L = (x1 - x0) - (count - 1) * gapPx;
    const band = L / count;
    if (band <= 0) throw new Error('generateShelfGrid: gap/padding leave no room for shelves');
    const bandH = y1 - y0;
    for (let i = 0; i < count; i++) {
      const rx0 = x0 + i * (band + gapPx);
      const rx1 = rx0 + band;
      const num = i + 1; // left band = num 1
      out.push({
        pointsNorm: cornersFor(rx0, y0, rx1, y1),
        perspective_width: Math.round(band),
        perspective_height: Math.round(bandH),
        name: ordinalName(num, naming),
        shelf_num_on_rack: num,
        bottom_points_indices: [2, 3],
        top_points_indices: [0, 1],
      });
    }
  }

  return out;
}
